import React, { useMemo } from 'react';

interface SignalStrengthIndicatorProps {
    rssiValue: number | null;
    snrValue: number | null;
}

export const SignalStrengthIndicator: React.FC<SignalStrengthIndicatorProps> = ({ rssiValue, snrValue }) => {
    // Calculate the combined signal quality with useMemo to prevent recalculations
    // Always call hooks at the top level, before any conditional returns
    const signalQuality = useMemo(() => {
        // If values are not available, return null
        if (rssiValue === null || snrValue === null) {
            return null;
        }
        
        const combinedVal = rssiValue + snrValue;
        
        if (combinedVal > -95) {
            return 'good';
        } else if (combinedVal < -120) {
            return 'bad';
        } else {
            return 'average';
        }
    }, [rssiValue, snrValue]);

    // Default to null if values are not available
    if (rssiValue === null || snrValue === null || signalQuality === null) {
        return null;
    }

    // Define styles with fixed dimensions to avoid resize observations
    const signalBarStyle = {
        width: '4px',
        marginRight: '1px',
        borderRadius: '1px'
    };

    const heightStyles = {
        small: { height: '10px' },
        medium: { height: '15px' },
        large: { height: '20px' }
    };

    const emptyBarStyle = {
        backgroundColor: '#e6e6e6'
    };

    // Wrapper styles with fixed dimensions
    const wrapperStyle = {
        height: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '0.5rem'
    };

    return (
        <div style={wrapperStyle} className="signal-strength-indicator">
            {signalQuality === 'good' && (
                <>
                    <div className="signal-bar bg-success" style={{ ...signalBarStyle, ...heightStyles.small }}></div>
                    <div className="signal-bar bg-success" style={{ ...signalBarStyle, ...heightStyles.medium }}></div>
                    <div className="signal-bar bg-success" style={{ ...signalBarStyle, ...heightStyles.large }}></div>
                </>
            )}
            {signalQuality === 'average' && (
                <>
                    <div className="signal-bar bg-warning" style={{ ...signalBarStyle, ...heightStyles.small }}></div>
                    <div className="signal-bar bg-warning" style={{ ...signalBarStyle, ...heightStyles.medium }}></div>
                    <div className="signal-bar" style={{ ...signalBarStyle, ...heightStyles.large, ...emptyBarStyle }}></div>
                </>
            )}
            {signalQuality === 'bad' && (
                <>
                    <div className="signal-bar bg-danger" style={{ ...signalBarStyle, ...heightStyles.small }}></div>
                    <div className="signal-bar" style={{ ...signalBarStyle, ...heightStyles.medium, ...emptyBarStyle }}></div>
                    <div className="signal-bar" style={{ ...signalBarStyle, ...heightStyles.large, ...emptyBarStyle }}></div>
                </>
            )}
        </div>
    );
}; 